import { Grid } from '@mui/material';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { AllocationRule } from 'further-types/lp/allocations';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import { Controller } from 'react-hook-form';
import { useDebounce } from 'usehooks-ts';
import {
  CapitalActionFormState,
  UpdateCapitalActionFormValues,
} from '../../hooks/useCapitalActionForm';
import CapitalCallAllocations from './CapitalCallAllocations';

type Props = {
  index: number;
  vehicleId: string;
  entryType: CapitalActionEntryType;
  baseCurrency: CurrencyCode;
};

const GeneralCapitalCallDetails: React.FC<Props> = ({
  index,
  vehicleId,
  baseCurrency,
}) => {
  const {
    formMethods: { control, watch, setValue },
  } = useFormWithStateContext<
    UpdateCapitalActionFormValues,
    CapitalActionFormState
  >();

  const debouncedTotalAmount = useDebounce(
    watch(`entries.${index}.totalAmountLcy`),
    300,
  );

  const allocationRule = watch(`entries.${index}.allocationRule`);
  const capitalBreakdown = watch(`entries.${index}.capitalBreakdown`);

  const handleAllocationsChange = (
    allocations: Array<{
      commitmentId: string;
      amountLcy: number;
      fundsCalled?: {
        fromCommitmentLcy: number;
        fromRecallableDistributionLcy: number;
      };
    }>,
  ) => {
    setValue(
      `entries.${index}.capitalBreakdown`,
      allocations.map((allocation) => ({
        commitmentId: allocation.commitmentId,
        amountLcy: allocation.amountLcy,
        fundsCalled: {
          fromCommitmentLcy: allocation.fundsCalled?.fromCommitmentLcy ?? 0,
          fromRecallableDistributionLcy:
            allocation.fundsCalled?.fromRecallableDistributionLcy ?? 0,
        },
      })),
    );
  };

  return (
    <>
      <Grid container>
        <Grid size={{ sm: 12, md: 12, lg: 8 }}>
          <Column spacing="md">
            <FormField label="Funds to call" size="md">
              <Controller
                name={`entries.${index}.totalAmountLcy`}
                control={control}
                render={({
                  fieldState: { error },
                  field: { onChange, ...rest },
                }) => (
                  <NumberInput
                    fullWidth
                    {...rest}
                    onChange={(value) => {
                      onChange(value ?? 0);
                    }}
                    decimals={5}
                    error={!!error}
                    helperText={error?.message?.toString()}
                    placeholder="Enter amount"
                    currency={baseCurrency}
                  />
                )}
              />
            </FormField>
            <FormField label="Allocation rule" size="md">
              <Controller
                name={`entries.${index}.allocationRule`}
                control={control}
                render={({ fieldState: { error }, field }) => (
                  <BasicSelect
                    {...field}
                    fullWidth
                    options={[
                      {
                        value: AllocationRule.ByCommitmentAndClosingDate,
                        label: `By commitment and closing date`,
                      },
                      {
                        value: AllocationRule.Manual,
                        label: 'Manual allocation',
                      },
                    ]}
                    error={!!error}
                    helperText={error?.message?.toString()}
                  />
                )}
              />
            </FormField>
          </Column>
        </Grid>
      </Grid>

      <CapitalCallAllocations
        entryIndex={index}
        showUncalledCommitment
        calledFundsLabel="General capital call"
        title="General capital"
        currency={baseCurrency}
        allocationRule={
          allocationRule ?? AllocationRule.ByCommitmentAndClosingDate
        }
        onChange={handleAllocationsChange}
        capitalBreakdown={capitalBreakdown ?? []}
        capitalCalledLcy={debouncedTotalAmount ?? 0}
        vehicleId={vehicleId}
        keepCallAmountFixed={allocationRule !== AllocationRule.Manual}
      />
    </>
  );
};

export default GeneralCapitalCallDetails;
